import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermission';
import {
  getNotificationCounts,
  getNotifications,
  markNotificationRead,
} from '../../services/notificationService';
import { resolveUploadUrl } from '../../utils/apiClient';
import styles from './Topbar.module.css';
import ProjectsIcon from '../../assets/qa_project.png';
import ScheduleIcon from '../../assets/qa_schedule.png';
import TimesheetsIcon from '../../assets/qa_time.png';
import LeaveIcon from '../../assets/qa_leave.png';
import notificationIcon from '../../assets/qa_notification.png';

export default function Topbar({ user }) {
  const navigate = useNavigate();
  const [allNotificationCount, setAllNotificationCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const logoUrl = resolveUploadUrl(user?.company_logo_url) || '/favicon.png';
  const companyName = user?.company_name || 'Company';
  const {
    canCreateProjects,
    canCreateBookings,
    canCreateTimesheets,
    canCreateOwnTimeOff,
    canApproveTimeOff,
  } = usePermissions();

  const timeOffLabel = canApproveTimeOff
    ? '+ Schedule Leave'
    : canCreateOwnTimeOff
      ? '+ Request Leave'
      : null;

  useEffect(() => {
    let isMounted = true;

    const fetchCounts = async () => {
      try {
        const counts = await getNotificationCounts();
        if (isMounted) {
          setAllNotificationCount(counts.all || 0);
        }
      } catch (error) {
        // Keep UI responsive even if notifications fail
      }
    };

    fetchCounts();
    const intervalId = setInterval(fetchCounts, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const fetchUnreadNotifications = async () => {
      try {
        const data = await getNotifications({ unreadOnly: true, limit: 8, scope: 'all' });
        setUnreadNotifications(data);
      } catch (error) {
        setUnreadNotifications([]);
      }
    };

    fetchUnreadNotifications();
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatNotificationTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString();
  };

  const refreshCount = async () => {
    try {
      const counts = await getNotificationCounts();
      setAllNotificationCount(counts.all || 0);
    } catch (error) {
      // no-op
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setUnreadNotifications((prev) => prev.filter((n) => n.notification_id !== notificationId));
      await refreshCount();
    } catch (error) {
      // no-op
    }
  };

  const handleNotificationItemClick = async (notification) => {
    await handleMarkRead(notification.notification_id);
    setIsDropdownOpen(false);
    if (notification.action_path) {
      navigate(notification.action_path);
      return;
    }
    navigate('/dashboard/notifications');
  };

  const openArchive = () => {
    setIsDropdownOpen(false);
    navigate('/dashboard/notifications');
  };

  return (
    <header className={styles.topbar}>
      <div
        className={styles.logoSection}
        onClick={() => navigate('/dashboard/')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/dashboard/');
          }
        }}
        aria-label="Go to dashboard"
      >
        <img src={logoUrl} alt="Company Logo" className={styles.logo} />
        <span className={styles.companyName}>{companyName}</span>
      </div>

      <div className={styles.actionsSection}>
        {canCreateProjects && (
          <button
            className={styles.actionBtn}
            type="button"
            aria-label="New Project"
            title="New Project"
            onClick={() =>
              navigate('/dashboard/projects', {
                state: { openNewProjectModal: true },
              })
            }
          >
            <img src={ProjectsIcon} alt="" aria-hidden="true" className={styles.actionIcon} />
          </button>
        )}
        {canCreateBookings && (
          <button
            className={styles.actionBtn}
            type="button"
            aria-label="Assign Resource"
            title="Assign Resource"
            onClick={() => navigate('/dashboard/schedule')}
          >
            <img src={ScheduleIcon} alt="" aria-hidden="true" className={styles.actionIcon} />
          </button>
        )}
        {canCreateTimesheets && (
          <button
            className={styles.actionBtn}
            type="button"
            aria-label="Log Time"
            title="Log Time"
            onClick={() => navigate('/dashboard/timesheets')}
          >
            <img src={TimesheetsIcon} alt="" aria-hidden="true" className={styles.actionIcon} />
          </button>
        )}
        {timeOffLabel && (
          <button
            className={styles.actionBtn}
            type="button"
            aria-label={timeOffLabel.replace('+ ', '')}
            title={timeOffLabel.replace('+ ', '')}
            onClick={() => navigate('/dashboard', { state: { openTimeOffModal: true } })}
          >
            <img src={LeaveIcon} alt="" aria-hidden="true" className={styles.actionIcon} />
          </button>
        )}

        <div className={styles.notificationCluster} ref={dropdownRef}>
          <button
            className={styles.notificationBtn}
            type="button"
            aria-label="Notifications"
            title="Unread notifications"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            <img src={notificationIcon} alt="Notifications" className={styles.actionIcon} />
            {allNotificationCount > 0 && (
              <>
                <span className={styles.bellPulse} aria-hidden="true" />
                <span className={styles.notificationBadge}>
                  {allNotificationCount > 99 ? '99+' : allNotificationCount}
                </span>
              </>
            )}
          </button>

          <button
            className={styles.archiveBtn}
            type="button"
            aria-label="Notifications archive"
            title="Notifications archive"
            onClick={openArchive}
          >
            Archive
          </button>

          {isDropdownOpen && (
            <div className={styles.notificationDropdown} role="menu" aria-label="Unread notifications">
              <div className={styles.dropdownHeader}>
                <span>Unread notifications</span>
                <button type="button" className={styles.dropdownArchiveLink} onClick={openArchive}>
                  View all
                </button>
              </div>

              {unreadNotifications.length === 0 ? (
                <div className={styles.emptyState}>No unread notifications</div>
              ) : (
                unreadNotifications.map((notification) => (
                  <div key={notification.notification_id} className={styles.dropdownItem}>
                    <button
                      type="button"
                      className={styles.itemMain}
                      onClick={() => handleNotificationItemClick(notification)}
                    >
                      <span className={styles.itemTitle}>{notification.title}</span>
                      {notification.message ? (
                        <span className={styles.itemMessage}>{notification.message}</span>
                      ) : null}
                      <span className={styles.itemMeta}>{formatNotificationTime(notification.created_at)}</span>
                    </button>
                    <button
                      type="button"
                      className={styles.itemMarkRead}
                      onClick={() => handleMarkRead(notification.notification_id)}
                      title="Mark as read"
                    >
                      Done
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
